import { standardUnits, StandardUnit } from '../types/units';
import { categoryTranslations } from '../types/categories';
import { IngredientCategory } from '../types/ingredients';
import { mapToStandardUnit } from './unitMapping';

/**
 * Get translated unit name
 * Automatically maps abbreviated units (g, ml, pcs) to standard units first
 */
export function getUnitLabel(unit: string, language: 'en' | 'fr' = 'fr'): string {
  // Try direct lookup first
  let unitKey = unit as StandardUnit;
  if (standardUnits[unitKey]) {
    return standardUnits[unitKey][language];
  }
  
  // Try mapping abbreviated unit to standard unit
  const mappedUnit = mapToStandardUnit(unit);
  if (standardUnits[mappedUnit]) {
    return standardUnits[mappedUnit][language];
  }
  
  // Fallback to original unit string
  return unit;
}

/**
 * Get unit abbreviation
 * Automatically maps abbreviated units (g, ml, pcs) to standard units first
 */
export function getUnitAbbreviation(unit: string): string {
  // Try direct lookup first
  let unitKey = unit as StandardUnit;
  if (standardUnits[unitKey]) {
    return standardUnits[unitKey].abbreviation;
  }
  
  // Try mapping abbreviated unit to standard unit
  const mappedUnit = mapToStandardUnit(unit);
  if (standardUnits[mappedUnit]) {
    return standardUnits[mappedUnit].abbreviation;
  }
  
  // Fallback to original unit string
  return unit;
}

/**
 * Get translated category name
 */
export function getCategoryLabel(category: IngredientCategory, language: 'en' | 'fr' = 'fr'): string {
  return categoryTranslations[category]?.[language] || category;
}

/**
 * Format ingredient with amount and unit
 */
export function formatIngredient(amount: number, unit: string, name: string, language: 'en' | 'fr' = 'fr'): string {
  if (amount <= 0) return name;
  
  const unitLabel = getUnitLabel(unit, language);
  return `${amount} ${unitLabel} ${name}`;
}

/**
 * Format ingredient with abbreviation
 */
export function formatIngredientShort(amount: number, unit: string, name: string): string {
  if (amount <= 0) return name;
  
  const unitAbbr = getUnitAbbreviation(unit);
  return `${amount} ${unitAbbr} ${name}`;
}

